import React, { useState, useEffect } from 'react';
import { CalendarDays, Loader2, MapPin, Building2 } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../services/api/axios';
import { useSearchParams } from 'react-router-dom';
import { fetchProjects } from '../services/projects';
import { getMediaUrl } from '../utils/media';

const SiteVisit = () => {
  const [loading, setLoading] = useState(false);
  const [projectDetails, setProjectDetails] = useState(null);
  const [projectImage, setProjectImage] = useState(null);
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('leadId');
  const projectId = searchParams.get('projectId');
 

  const [formData, setFormData] = useState({
    preferredDate: '',
    preferredTime: '',
  });

  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!projectId) return;

      try {
        // Fetch project details using fetchProjects
        const projects = await fetchProjects({ projectId });
        if (projects && projects.length > 0) {
          setProjectDetails(projects[0]);
        }

        // Fetch project image
        const mediaResponse = await axiosInstance.get("/projects/media", {
          params: {
            project_id: projectId,
            images: true,
          },
        });
        
        if (mediaResponse && mediaResponse.length > 0) {
          setProjectImage(mediaResponse[0]);
        }
      } catch (error) {
        console.error("Error fetching project details:", error);
        toast.error("Failed to load project details");
      }
    };

    fetchProjectDetails();
  }, [projectId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const visitSchedule = `${formData.preferredDate}T${formData.preferredTime}:00`;
      
      const visitData = {
        leadId: leadId,
        projectId: projectId,
        visitSchedule: visitSchedule,  
      };

      await axiosInstance.post('/users/schedule-visit', visitData);
      toast.success("Site visit scheduled successfully!");
      
      // Reset form
      setFormData({
        preferredDate: '',
        preferredTime: '',
      });
    } catch (error) {
      console.error("Error scheduling visit:", error);
      toast.error("Failed to schedule visit");
    } finally {
      setLoading(false);
    }
  };

  // Get today's date in YYYY-MM-DD format for min date in date input
  const today = new Date().toISOString().split('T')[0];

  if (!leadId || !projectId) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-xl text-red-600 font-semibold">Invalid URL</h2>
          <p className="mt-2 text-gray-600">Missing required parameters. Please check the URL.</p>
        </div>
      </div>
    );
  }

  // Function to get template type display name
  const getTemplateTypeDisplay = () => {
    const types = {
      'birthday_template': 'Birthday Visit',
      'festival_template': 'Festival Visit',
      'standard': 'Site Visit'
    };
    return  'Site Visit';
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {projectDetails && (
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 mb-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Project Image */}
              <div className="w-full md:w-1/3">
                {projectImage ? (
                  <img
                    src={getMediaUrl(projectImage)}
                    alt={projectDetails.projectName}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Building2 className="h-12 w-12 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Project Details */}
              <div className="w-full md:w-2/3">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {projectDetails.projectName}
                </h2>
                <p className="text-gray-600 mb-2">
                  <span className="font-semibold">Developer:</span> {projectDetails.developer}
                </p>
                {projectDetails.address && (
                  <div className="flex items-start gap-2 text-gray-600">
                    <MapPin className="h-5 w-5 mt-1" />
                    <p>
                      {projectDetails.address.buildingNo} {projectDetails.address.streetName1}
                      {projectDetails.address.streetName2 && `, ${projectDetails.address.streetName2}`},
                      {projectDetails.address.district}, {projectDetails.address.city},
                      {projectDetails.address.state} {projectDetails.address.pinCode}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Schedule a {getTemplateTypeDisplay()}</h1>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Visit Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    min={today}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <CalendarDays className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Time
                </label>
                <input
                  type="time"
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                    Scheduling...
                  </>
                ) : (
                  'Schedule Visit'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SiteVisit; 